/**
 * 霓虹蛇境 (Neon Snake Realm) - 道具效果显示组件
 *
 * 显示当前激活的道具效果徽章，包含图标、名称和剩余时间。
 * 从 App.tsx 中提取的纯展示组件，无内部状态。
 *
 * 使用方式：
 * ```tsx
 * <ActiveEffects effects={gameState.activeEffects} />
 * ```
 */

import React from 'react';
import type { ActiveEffect } from '@/types/game';
import { PowerUpType } from '@/types/game';
import { COLOR_SCHEME } from '@/game/constants';

// ============================================================================
// Props 类型定义
// ============================================================================

/**
 * ActiveEffects 组件的 Props 接口。
 *
 * 组件仅依赖激活效果数组，不包含任何回调或事件处理。
 * 当效果数组为空时，组件返回 null 不渲染任何 DOM 元素。
 */
export interface ActiveEffectsProps {
  /** 当前激活的道具效果列表 */
  effects: ActiveEffect[];
}

// ============================================================================
// 常量定义
// ============================================================================

/**
 * 道具类型到显示文本的映射表。
 *
 * 使用 emoji 图标增强视觉辨识度，中文文字提供明确含义。
 * 映射表在模块顶层定义为常量，避免每次渲染时重新创建对象。
 */
const EFFECT_LABELS: Record<PowerUpType, string> = {
  [PowerUpType.Speed]: '⚡ 加速',
  [PowerUpType.Slow]: '🐢 减速',
  [PowerUpType.WallPass]: '🔮 穿墙',
  [PowerUpType.DoubleScore]: '✨ 双倍',
  [PowerUpType.Shrink]: '📏 缩小',
  [PowerUpType.ClearObstacles]: '🧹 清除',
};

// ============================================================================
// ActiveEffects 组件
// ============================================================================

/**
 * 道具效果显示组件。
 *
 * 渲染当前激活的道具效果为徽章列表，每个徽章包含：
 * - 道具图标和名称
 * - 剩余持续时间（秒）
 *
 * 组件为纯展示组件，不包含任何业务逻辑或副作用。
 * 所有样式通过全局 CSS 类名控制，颜色通过内联样式动态设置。
 *
 * 性能优化：
 * - 当 effects 数组为空时返回 null，避免渲染空 div
 * - 使用 key 属性确保 React 正确追踪每个徽章
 * - 使用 `${effect.type}-${index}` 作为 key，处理同类型效果共存的情况
 *
 * @param props - 组件属性
 * @returns 道具效果徽章列表的 JSX 元素，或 null（无效果时）
 */
const ActiveEffects: React.FC<ActiveEffectsProps> = ({ effects }) => {
  // 无激活效果时不渲染任何内容
  if (effects.length === 0) {
    return null;
  }

  return (
    <div className="active-effects">
      {effects.map((effect, index) => {
        // 获取当前效果类型对应的颜色
        const effectColor =
          COLOR_SCHEME.powerUpColors[effect.type] || '#ffffff';

        // 计算剩余秒数（向上取整，确保显示至少 1 秒）
        const remainingSeconds = Math.ceil(effect.remainingTime / 1000);

        return (
          <div
            key={`${effect.type}-${index}`}
            className="effect-badge"
            style={{
              borderColor: effectColor,
              color: effectColor,
            }}
          >
            <span className="effect-icon">
              {EFFECT_LABELS[effect.type]}
            </span>
            <span className="effect-timer">
              {remainingSeconds}s
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// 导出
// ============================================================================

export default ActiveEffects;